import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Plus, KeyRound, ShieldCheck, UserPlus, CornerDownRight, GitBranch, MapPin, MapPinOff, EyeOff, Eye, Pencil, Lock, Users as UsersIcon, Target, ArrowUpDown, X, UserCog, UserCheck, History, Replace } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { splitIds } from "../../components/TerritoryFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg } from "../../lib/api";
import { PASSWORD_RULES_HINT } from "../../components/ForgotPasswordDialog";
import { UserAccessDialog } from "../../components/UserAccessDialog";
import { TerritoryDialog } from "../../components/TerritoryDialog";
import { BulkUnassignDialog } from "../../components/BulkUnassignDialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { SupervisorTargetsTab } from "../../components/SupervisorTargetsTab";
import { CollectorTargetsTab } from "../../components/CollectorTargetsTab";
import { PaginationBar } from "../../components/PaginationBar";
import { StaffAvatar, EmpCode, StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { AvatarEditor } from "../../components/AvatarEditor";
import { StaffActivitySheet } from "../../components/StaffActivitySheet";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel, ReadOnlyNotice, ViewOnlyBadge } from "../../components/FeatureGate";
import { PlaceholderBadge, PlaceholderCollectorsPanel, ReplacePlaceholderDialog } from "../../components/PlaceholderCollectors";

// Phase 86: directory sort options
const SORTS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "role", label: "Role" },
  { value: "supervisor", label: "Supervisor" },
  { value: "recent", label: "Recently added" },
];
const ROLE_ORDER = { municipal_admin: 0, supervisor: 1, tax_collector: 2 };
// Phase 215: citizens are managed in the dedicated Citizens module — this directory is staff only
const isStaffRow = (u) => u.role !== "citizen";

const empty = { name: "", email: "", mobile: "", password: "", role: "tax_collector", employee_id: "", designation: "", reports_to_id: "", custom_data: {} };
const emptySub = { name: "", email: "", mobile: "", password: "", employee_id: "", designation: "", can_approve: false };
const STAFF_ROLES = ["municipal_admin", "supervisor", "tax_collector"];
// reporting hierarchy: role -> [manager role, label]
const MAPPING_RULES = {
  tax_collector: ["supervisor", "Supervisor"],
  supervisor: ["municipal_admin", "Municipal Admin"],
};
// which portal a base role template signs into (custom roles inherit this)
const PORTAL_HINT = {
  municipal_admin: "signs into the desktop Admin Console",
  supervisor: "signs into the desktop Supervisor Console",
  tax_collector: "signs into the mobile Field Agent app",
};

// Dynamic per-municipality staff fields (Phase 60) — shared by create + edit forms
export function CustomFieldInputs({ fields, values, onChange, idPrefix = "custom-field" }) {
  if (!fields || fields.length === 0) return null;
  const set = (key, v) => onChange({ ...(values || {}), [key]: v });
  return (
    <div className="space-y-3 border rounded-lg p-3 bg-muted/30" data-testid={`${idPrefix}-section`}>
      <p className="text-xs font-medium text-muted-foreground">Additional details (set by your municipality)</p>
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
          {f.field_type === "select" ? (
            <Select value={values?.[f.key] != null ? String(values[f.key]) : ""} onValueChange={(v) => set(f.key, v)}>
              <SelectTrigger data-testid={`${idPrefix}-${f.key}`}>
                <SelectValue placeholder={`Select ${f.label}`} />
              </SelectTrigger>
              <SelectContent>
                {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={f.field_type === "number" ? "number" : f.field_type === "date" ? "date" : "text"}
              value={values?.[f.key] != null ? values[f.key] : ""}
              onChange={(e) => set(f.key, e.target.value)}
              data-testid={`${idPrefix}-${f.key}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export function ResetPasswordDialog({ open, onOpenChange, targetLabel, onSubmit }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const handleOpenChange = (v) => { if (!v) { setPw1(""); setPw2(""); } onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await onSubmit(pw1);
      handleOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Reset password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {targetLabel && <p className="text-sm text-muted-foreground">Setting a new password for <span className="font-medium text-foreground">{targetLabel}</span></p>}
          <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2" data-testid="reset-force-change-note">
            The user will be asked to set their own password at next login.
          </p>
          <div className="space-y-1">
            <Label>New password</Label>
            <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required data-testid="reset-new-password-input" />
            <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
          </div>
          <div className="space-y-1">
            <Label>Confirm new password</Label>
            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-confirm-password-input" />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="reset-password-submit-button">
            {busy ? "Resetting..." : "Reset password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MappingDialog({ code, user, allUsers, customRoles, onOpenChange, onSaved }) {
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  // Phase 71: a custom role may override the reporting target
  const cr = user?.custom_role_id ? (customRoles || []).find((r) => r.id === user.custom_role_id) : null;
  const crRule = cr?.reports_to && MAPPING_RULES[user?.role]
    ? [cr.reports_to, cr.reports_to === "municipal_admin" ? "Municipal Admin" : "Supervisor"]
    : null;
  const rule = user ? (crRule || MAPPING_RULES[user.role]) : null;
  const options = rule
    ? allUsers.filter((u) => u.role === rule[0] && u.is_active !== false && u.id !== user?.id)
    : [];

  useEffect(() => { setTargetId(user?.reports_to_id || ""); }, [user]);

  const save = async () => {
    if (!targetId) return toast.error(`Please select a ${rule?.[1]}`);
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/users/${user.id}/mapping`, { reports_to_id: targetId });
      const extra = r.data.cascaded_sub_users ? ` (applied to ${r.data.cascaded_sub_users} sub-user${r.data.cascaded_sub_users === 1 ? "" : "s"} too)` : "";
      toast.success(`${staffLabelText(user.name, user.employee_id)} now reports to ${r.data.reports_to_name}${extra}`);
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="mapping-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> Change mapping — {staffLabelText(user?.name, user?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2">
            {user?.role === "tax_collector"
              ? "This Tax Collector will report to the selected Supervisor. Supervisor screens (assignments, tracking, day collections) show only their mapped collectors."
              : "This Supervisor will report to the selected Municipal Admin."}
            {" "}Sub-users of this user follow the same mapping automatically.
          </p>
          {user?.role === "tax_collector" && (
            <p className="text-xs text-muted-foreground rounded-md border px-3 py-2" data-testid="mapping-auto-hint">
              Collectors follow their wards: whenever a supervisor's territory or this collector's wards change, "Reports to" is
              re-pointed automatically to the supervisor who owns most of the collector's wards
              {user?.reports_to_auto ? " (currently set automatically)" : ""}.
            </p>
          )}
          <div className="space-y-1">
            <Label>Reports to ({rule?.[1]})</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger data-testid="mapping-target-select"><SelectValue placeholder={`Select ${rule?.[1]}`} /></SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{staffLabelText(o.name, o.employee_id)}{o.designation ? ` • ${o.designation}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={save} disabled={busy} data-testid="mapping-save-button">
            {busy ? "Saving..." : "Save mapping"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddSubUserDialog({ code, parent, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptySub);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/t/${code}/users/${parent.id}/sub-users`, form);
      toast.success(`Sub-user created under ${staffLabelText(parent.name, parent.employee_id)} — role locked to ${(parent.role || "").replace(/_/g, " ")}, access inherited`);
      setForm(emptySub);
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!parent} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-sub-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> New sub-user under {staffLabelText(parent?.name, parent?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2">
            Role is locked to <span className="font-medium capitalize">{parent?.custom_role_name || (parent?.role || "").replace(/_/g, " ")}</span> (same as the parent).
            The parent's current menu access is copied to this sub-user and can be changed later.
          </p>
          <div className="space-y-1"><Label>Full name</Label><Input value={form.name} onChange={set("name")} required data-testid="sub-user-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="sub-user-email-input" /></div>
            <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} maxLength={10} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Employee ID</Label><Input value={form.employee_id} onChange={set("employee_id")} /></div>
            <div className="space-y-1"><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} /></div>
          </div>
          <div className="space-y-1">
            <Label>Password</Label>
            <Input type="password" value={form.password} onChange={set("password")} required data-testid="sub-user-password-input" />
            <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
          </div>
          {parent?.role !== "municipal_admin" && (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.can_approve} onCheckedChange={(v) => setForm({ ...form, can_approve: v })} data-testid="sub-user-approver-switch" />
              Grant workflow approval rights
            </label>
          )}
          <Button type="submit" className="w-full" disabled={busy} data-testid="sub-user-submit-button">
            {busy ? "Creating..." : "Create sub-user"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit profile details of a staff user. Main Municipal Admin accounts are
// protected — only the Super Admin can edit them (enforced server-side too).
export function EditUserDialog({ code, user, customFields, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", employee_id: "", designation: "", custom_data: {} });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", email: user.email || "", mobile: user.mobile || "",
        employee_id: user.employee_id || "", designation: user.designation || "",
        custom_data: user.custom_data || {} });
    }
  }, [user]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/t/${code}/users/${user.id}`, form);
      toast.success(`${staffLabelText(form.name || user.name, form.employee_id ?? user.employee_id)} updated`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="h-4 w-4" /> {user?.role === "citizen" ? "Edit citizen" : "Edit user"}
          </DialogTitle>
        </DialogHeader>
        {user && (
          <form onSubmit={submit} className="space-y-3">
            {STAFF_ROLES.includes(user.role) && (
              <div className="rounded-lg border bg-muted/30 p-3" data-testid="edit-user-photo-section">
                <AvatarEditor code={code} userId={user.id} name={form.name || user.name} photoUrl={user.photo_url || null}
                  compact size="lg" testId="edit-user-photo" onChanged={() => onSaved()} />
                <p className="text-[11px] text-muted-foreground mt-2">Photo saves immediately and appears wherever this person's name is shown.</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground bg-accent/50 rounded-md px-3 py-2">
              Role: <span className="font-medium capitalize">{user.custom_role_name || user.role?.replace(/_/g, " ")}</span> (role cannot be changed here)
            </p>
            <div className="space-y-1">
              <Label>Full name</Label>
              <Input value={form.name} onChange={set("name")} required data-testid="edit-user-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} required data-testid="edit-user-email-input" />
              </div>
              <div className="space-y-1">
                <Label>Mobile</Label>
                <Input value={form.mobile} onChange={set("mobile")} maxLength={10} data-testid="edit-user-mobile-input" />
              </div>
            </div>
            {user.role !== "citizen" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Employee ID</Label>
                <Input value={form.employee_id} onChange={set("employee_id")} data-testid="edit-user-employee-input" />
              </div>
              <div className="space-y-1">
                <Label>Designation</Label>
                <Input value={form.designation} onChange={set("designation")} data-testid="edit-user-designation-input" />
              </div>
            </div>
            )}
            {user.role !== "citizen" && (
              <CustomFieldInputs fields={customFields} values={form.custom_data}
                onChange={(cd) => setForm({ ...form, custom_data: cd })} idPrefix="edit-custom-field" />
            )}
            <Button type="submit" className="w-full" disabled={busy} data-testid="edit-user-save-button">
              {busy ? "Saving..." : "Save changes"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Phase 152: Users & Staff tabs <-> "users" module sub-features
const TABS = [
  { value: "staff", feature: "staff" },
  { value: "targets", feature: "supervisor_targets" },
  { value: "collector-targets", feature: "collector_targets" },
];

export default function UsersPage() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const access = useRoleModules(code);
  const show = (f) => access.featureAllowed("users", f);
  const ro = (f) => !access.canWrite("users", f);
  const staffRO = ro("staff");
  const visibleTabs = TABS.filter((t) => show(t.feature));
  const [tab, setTab] = useState("staff");
  useEffect(() => {
    if (!access.loaded) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "users", TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length]);
  const [users, setUsers] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetTarget, setResetTarget] = useState(null);
  const [subParent, setSubParent] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null); // Phase 90
  const [mappingTarget, setMappingTarget] = useState(null);
  const [territoryTarget, setTerritoryTarget] = useState(null);
  // Phase 163: placeholder collectors — "Replace with real collector" dialog + panel refresh after merge / convert
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [phRefresh, setPhRefresh] = useState(0);
  // Phase 167: "Assign real collector" from a placeholder ward row → Territory dialog opens with that ward ticked
  const [territoryPreset, setTerritoryPreset] = useState(null);
  // Phase 159: checkbox multi-select (supervisors + collectors) -> "Unassign All Territories"
  const [selected, setSelected] = useState(() => new Set());
  const [bulkUnassignOpen, setBulkUnassignOpen] = useState(false);
  const [hideInactive, setHideInactive] = useState(() => localStorage.getItem(`hide_inactive_users_${code}`) === "1");
  // Phase 83: staff directory search + client-side pagination over grouped rows
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userLimit, setUserLimit] = useState(25);
  // Phase 60: per-municipality custom roles + custom staff-form fields
  const [cfg, setCfg] = useState({ custom_roles: [], custom_fields: [] });
  // Phase 86: territory / supervisor filters, sort, pending photo for a new user
  const [zones, setZones] = useState([]);
  const [wards, setWards] = useState([]);
  const [mohallas, setMohallas] = useState([]);   // Phase 158: ward → mohalla map tells whole-ward from mohalla-level coverage
  // Phase 92: deep-link from the Unassigned Wards report (?zone_id=… / ?ward_id=…) pre-fills the territory filters
  // Phase 176: both filters are multi-select (arrays of ids; the deep-link may carry a comma-joined list)
  const [zoneF, setZoneF] = useState(() => splitIds(searchParams.get("zone_id")));
  const [wardF, setWardF] = useState(() => splitIds(searchParams.get("ward_id")));
  const [supF, setSupF] = useState("");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(`users_sort_${code}`) || "name");
  const [pendingPhoto, setPendingPhoto] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/masters/zones`).then((r) => setZones(r.data || [])).catch(() => {});
    api.get(`/t/${code}/masters/wards`).then((r) => setWards(r.data || [])).catch(() => {});
    api.get(`/t/${code}/masters/mohallas`).then((r) => setMohallas(r.data || [])).catch(() => {});
  }, [code]);
  const zoneName = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z.zone_name])), [zones]);
  const wardName = useMemo(() => Object.fromEntries(wards.map((w) => [w.id, w.ward_name])), [wards]);
  const mohallaWard = useMemo(() => Object.fromEntries(mohallas.map((m) => [m.id, m.ward_id])), [mohallas]);
  const wardZone = useMemo(() => Object.fromEntries(wards.map((w) => [w.id, w.zone_id])), [wards]);
  const wardsForZone = zoneF.length ? wards.filter((w) => zoneF.includes(w.zone_id)) : wards;
  const changeZoneF = (ids) => {
    const allowed = new Set((ids.length ? wards.filter((w) => ids.includes(w.zone_id)) : wards).map((w) => w.id));
    setZoneF(ids); setWardF(wardF.filter((id) => allowed.has(id))); setUserPage(1);
  };
  const setSort = (v) => { setSortBy(v); localStorage.setItem(`users_sort_${code}`, v); };
  const resetFilters = () => { setZoneF([]); setWardF([]); setSupF(""); setUserSearch(""); setUserPage(1); };
  const filtersActive = !!(zoneF.length || wardF.length || supF);

  useEffect(() => {
    api.get(`/t/${code}/config/staff-form`)
      .then((r) => setCfg({ custom_roles: r.data.custom_roles || [], custom_fields: r.data.custom_fields || [] }))
      .catch(() => {});
  }, [code]);

  // role select uses "custom:{id}" values for custom roles; resolve to the base template
  const customRoleOf = (roleVal) =>
    roleVal && roleVal.startsWith("custom:") ? cfg.custom_roles.find((r) => `custom:${r.id}` === roleVal) : null;
  const baseRoleOf = (roleVal) => customRoleOf(roleVal)?.base_role || roleVal;
  // Phase 71: a custom role may override who it reports to (e.g. field agent -> Municipal Admin)
  const ruleFor = (roleVal) => {
    const cr = customRoleOf(roleVal);
    const base = baseRoleOf(roleVal);
    if (cr?.reports_to && MAPPING_RULES[base]) {
      return [cr.reports_to, cr.reports_to === "municipal_admin" ? "Municipal Admin" : "Supervisor"];
    }
    return MAPPING_RULES[base];
  };

  const setHide = (v) => {
    setHideInactive(v);
    setUserPage(1);
    localStorage.setItem(`hide_inactive_users_${code}`, v ? "1" : "0");
  };

  const load = useCallback(() => api.get(`/t/${code}/users`).then((r) => setUsers((r.data || []).filter(isStaffRow))), [code]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const cr = customRoleOf(form.role);
    const baseRole = baseRoleOf(form.role);
    const rule = ruleFor(form.role);
    if (rule && !form.reports_to_id) {
      return toast.error(`Please select the ${rule[1]} this user reports to`);
    }
    if (baseRole !== "citizen") {
      for (const f of cfg.custom_fields) {
        const v = form.custom_data?.[f.key];
        if (f.required && (v == null || String(v).trim() === "")) {
          return toast.error(`'${f.label}' is required`);
        }
      }
    }
    try {
      const r = await api.post(`/t/${code}/users`, {
        ...form, role: baseRole,
        custom_role_id: cr ? cr.id : null,
        custom_data: baseRole === "citizen" ? {} : (form.custom_data || {}),
      });
      if (pendingPhoto && baseRole !== "citizen" && r.data?.id) {
        try { await api.put(`/t/${code}/users/${r.data.id}/photo`, { data: pendingPhoto }); }
        catch (pe) { toast.error(`User created, but the photo could not be saved: ${errMsg(pe)}`); }
      }
      toast.success(cr ? `User created with role "${cr.name}"` : "User created");
      setOpen(false);
      setForm(empty);
      setPendingPhoto(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = async (u) => {
    try {
      await api.put(`/t/${code}/users/${u.id}/toggle`);
      const nowActive = !u.is_active;
      if (nowActive) {
        toast.success(`${staffLabelText(u.name, u.employee_id)} activated`);
      } else {
        toast.success(hideInactive
          ? `${staffLabelText(u.name, u.employee_id)} deactivated — hidden from the list. Turn off "Hide inactive" to see them again.`
          : `${staffLabelText(u.name, u.employee_id)} deactivated. Use "Hide inactive" to hide such users from the list.`);
      }
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleApprover = async (u) => {
    try {
      const r = await api.put(`/t/${code}/users/${u.id}/approver`);
      toast.success(r.data.can_approve
        ? `${u.name} can now approve workflow cases`
        : `Approval rights removed for ${u.name}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!users) return <Spinner />;

  const inactiveCount = users.filter((u) => u.is_active === false).length;
  const needle = userSearch.trim().toLowerCase();
  const matches = (u) => !needle || [u.name, u.email, u.mobile, u.role, u.designation, u.employee_id, u.custom_role_name, u.is_placeholder ? "placeholder" : null]
    .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
  const byId = Object.fromEntries(users.map((u) => [u.id, u]));
  // sub-users inherit territory + supervisor from their parent
  const effective = (u) => (u.parent_user_id && byId[u.parent_user_id]) || u;
  const wardsOf = (u) => effective(u).ward_ids || [];
  const zonesOf = (u) => {
    const e = effective(u);
    if (e.role === "supervisor") return e.zone_ids || [];
    return [...new Set((e.ward_ids || []).map((w) => wardZone[w]).filter(Boolean))];
  };
  const supervisorOf = (u) => {
    const e = effective(u);
    if (e.role === "supervisor") return e.id;
    return e.reports_to_id || null;
  };
  // Phase 86 territory / supervisor isolation. Collectors are matched on their own wards;
  // supervisors on their zones. Matching collectors bring their supervisor along for context.
  // Phase 176: zoneF / wardF are lists — a user matches when ANY selected id matches.
  const territoryMatch = (u) => {
    if (!zoneF.length && !wardF.length) return true;
    if (wardF.length) {
      if (u.role === "tax_collector") return wardsOf(u).some((w) => wardF.includes(w));
      if (u.role === "supervisor") {
        // Phase 166: a supervisor holding specific wards of the zone matches only on those wards
        const e = effective(u);
        return wardF.some((wf) => {
          const z = wardZone[wf];
          if (!(e.zone_ids || []).includes(z)) return false;
          const pickedInZone = (e.ward_ids || []).filter((w) => wardZone[w] === z);
          return pickedInZone.length === 0 || pickedInZone.includes(wf);
        });
      }
      return false;
    }
    return zonesOf(u).some((z) => zoneF.includes(z));
  };
  const supervisorMatch = (u) => !supF || u.id === supF || supervisorOf(u) === supF;
  const base = (hideInactive ? users.filter((u) => u.is_active !== false) : users).filter(matches);
  let visibleUsers = base.filter((u) => territoryMatch(u) && supervisorMatch(u));
  if (zoneF.length || wardF.length) {
    // keep the supervisor rows of matching collectors (context), even if the supervisor has no territory
    const supIds = new Set(visibleUsers.filter((u) => u.role === "tax_collector").map((u) => supervisorOf(u)).filter(Boolean));
    const extra = base.filter((u) => supIds.has(u.id) && !visibleUsers.includes(u) && supervisorMatch(u));
    visibleUsers = [...visibleUsers, ...extra];
  }
  // Phase 86 totals — role headcounts (active, with inactive shown separately) for the current view
  // Phase 163: placeholder collectors are bookkeeping entries, counted separately from real collectors
  const countRole = (list, role) => list.filter((u) => u.role === role && !u.is_placeholder).length;
  const totals = {
    supervisors: countRole(users.filter((u) => u.is_active !== false), "supervisor"),
    supervisorsInactive: countRole(users.filter((u) => u.is_active === false), "supervisor"),
    collectors: countRole(users.filter((u) => u.is_active !== false), "tax_collector"),
    collectorsInactive: countRole(users.filter((u) => u.is_active === false), "tax_collector"),
    placeholders: users.filter((u) => u.is_placeholder && u.is_active !== false).length,
    shownSupervisors: countRole(visibleUsers, "supervisor"),
    shownCollectors: countRole(visibleUsers, "tax_collector"),
  };

  // group: each main user followed by their indented sub-users
  const subsByParent = {};
  visibleUsers.forEach((u) => {
    if (u.parent_user_id) (subsByParent[u.parent_user_id] = subsByParent[u.parent_user_id] || []).push(u);
  });
  const cmpName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  const supLabel = (u) => (u.role === "supervisor" ? u.name : u.reports_to_name) || "";
  const comparators = {
    name: cmpName,
    role: (a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || cmpName(a, b),
    // group each supervisor with their collectors: supervisor first, then their team A–Z; unassigned last
    supervisor: (a, b) => (supLabel(a) ? 0 : 1) - (supLabel(b) ? 0 : 1)
      || supLabel(a).localeCompare(supLabel(b), undefined, { sensitivity: "base" })
      || (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || cmpName(a, b),
    recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") || cmpName(a, b),
  };
  const mains = visibleUsers.filter((u) => !u.parent_user_id).sort(comparators[sortBy] || cmpName);
  const rows = [];
  mains.forEach((p) => {
    rows.push({ u: p, isSub: false });
    (subsByParent[p.id] || []).sort(cmpName).forEach((s) => rows.push({ u: s, isSub: true }));
    delete subsByParent[p.id];
  });
  // orphaned sub-users (parent deleted/missing/filtered out) still shown
  Object.values(subsByParent).flat().forEach((s) => rows.push({ u: s, isSub: true }));
  const territoryLine = (u) => {
    const e = effective(u);
    if (e.role === "supervisor") {
      // Phase 166: "Zone-1 (2 wards)" when the supervisor holds only some wards of the zone; plain name = whole zone
      const perZone = {};
      (e.ward_ids || []).forEach((w) => { const z = wardZone[w]; if (z) perZone[z] = (perZone[z] || 0) + 1; });
      const names = (e.zone_ids || []).filter((z) => zoneName[z])
        .map((z) => perZone[z] ? `${zoneName[z]} (${perZone[z]} ward${perZone[z] === 1 ? "" : "s"})` : zoneName[z]);
      return names.length ? { icon: "zone", text: names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "") } : null;
    }
    if (e.role === "tax_collector") {
      // "Ward 1 (2 mohallas)" when the collector holds specific mohallas; plain ward name = whole ward
      const perWard = {};
      (e.mohalla_ids || []).forEach((m) => { const w = mohallaWard[m]; if (w) perWard[w] = (perWard[w] || 0) + 1; });
      const names = (e.ward_ids || []).filter((w) => wardName[w])
        .map((w) => perWard[w] ? `${wardName[w]} (${perWard[w]} mohalla${perWard[w] === 1 ? "" : "s"})` : wardName[w]);
      return names.length ? { icon: "ward", text: names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "") } : null;
    }
    return null;
  };
  // Phase 83: paginate the grouped rows client-side (keeps parent + sub-users together)
  const pageCount = Math.max(1, Math.ceil(rows.length / userLimit));
  const curPage = Math.min(userPage, pageCount);
  const pageRows = rows.slice((curPage - 1) * userLimit, curPage * userLimit);

  // Phase 159: which rows can be ticked — staff with a territory (supervisors are listed but skipped by the action)
  const selectable = (u) => !!MAPPING_RULES[u.role] && !staffRO;
  const selectableIds = pageRows.filter(({ u }) => selectable(u)).map(({ u }) => u.id);
  const visibleIds = new Set(rows.map(({ u }) => u.id));
  const selectedVisible = [...selected].filter((id) => visibleIds.has(id));   // selection survives paging, not hidden filters
  const selectedCollectorsWithTerritory = selectedVisible.filter((id) => {
    const u = byId[id];
    return u && u.role === "tax_collector" && ((u.ward_ids || []).length > 0 || (u.mohalla_ids || []).length > 0);
  }).length;
  const allPageSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const somePageSelected = selectableIds.some((id) => selected.has(id));
  const toggleSelect = (id, on) => setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  const togglePage = (on) => setSelected((prev) => { const n = new Set(prev); selectableIds.forEach((id) => { if (on) n.add(id); else n.delete(id); }); return n; });
  const clearSelection = () => setSelected(new Set());

  return (
    <div>
      <PageHeader title="Users & Staff" subtitle="Manage staff, their sub-users, menu access and workflow approval rights — citizen accounts live under Citizens">
        {show("staff") && !staffRO && <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPendingPhoto(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button"><Plus className="h-4 w-4 mr-2" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New user</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              {baseRoleOf(form.role) !== "citizen" && (
                <div className="rounded-lg border bg-muted/30 p-3" data-testid="user-photo-section">
                  <AvatarEditor code={code} deferred value={pendingPhoto} onValueChange={setPendingPhoto}
                    name={form.name || "New user"} compact size="lg" testId="user-photo" />
                  <p className="text-[11px] text-muted-foreground mt-2">Optional — the photo is saved with the user and shown wherever their name appears.</p>
                </div>
              )}
              <div className="space-y-1"><Label>Full name</Label><Input value={form.name} onChange={set("name")} required data-testid="user-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="user-email-input" /></div>
                <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} maxLength={10} /></div>
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, reports_to_id: "" })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="municipal_admin">Municipal Admin</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="tax_collector">Tax Collector</SelectItem>
                    {cfg.custom_roles.length > 0 && (
                      <SelectItem value="__custom_header" disabled className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Custom roles
                      </SelectItem>
                    )}
                    {cfg.custom_roles.map((r) => (
                      <SelectItem key={r.id} value={`custom:${r.id}`} data-testid={`custom-role-option-${r.id}`}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {customRoleOf(form.role) && (
                  <p className="text-[11px] text-muted-foreground bg-accent/50 rounded-md px-2 py-1.5" data-testid="custom-role-hint">
                    {customRoleOf(form.role).description ? `${customRoleOf(form.role).description} — ` : ""}
                    This role {PORTAL_HINT[baseRoleOf(form.role)]}.
                  </p>
                )}
              </div>
              {ruleFor(form.role) && (
                <div className="space-y-1">
                  <Label className="flex items-center gap-1.5">
                    <GitBranch className="h-3.5 w-3.5" /> Reports to ({ruleFor(form.role)[1]}) <span className="text-destructive">*</span>
                  </Label>
                  <Select value={form.reports_to_id} onValueChange={(v) => setForm({ ...form, reports_to_id: v })}>
                    <SelectTrigger data-testid="user-reports-to-select">
                      <SelectValue placeholder={`Select ${ruleFor(form.role)[1]}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {(users || []).filter((u) => u.role === ruleFor(form.role)[0] && u.is_active !== false).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{staffLabelText(u.name, u.employee_id)}{u.designation ? ` • ${u.designation}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    {ruleFor(form.role)[0] === "supervisor"
                      ? "Mandatory — the supervisor this staff member works under."
                      : "Mandatory — the municipal admin this staff member works under."}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Employee ID</Label><Input value={form.employee_id} onChange={set("employee_id")} /></div>
                <div className="space-y-1"><Label>Designation</Label><Input value={form.designation} onChange={set("designation")} /></div>
              </div>
              <div className="space-y-1"><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} required data-testid="user-password-input" /></div>
              {baseRoleOf(form.role) !== "citizen" && (
                <CustomFieldInputs fields={cfg.custom_fields} values={form.custom_data}
                  onChange={(cd) => setForm({ ...form, custom_data: cd })} idPrefix="user-custom-field" />
              )}
              <Button type="submit" className="w-full" data-testid="user-submit-button">Create user</Button>
            </form>
          </DialogContent>
        </Dialog>}
      </PageHeader>

      {access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Users & Staff" testId="users-no-access" /> : (
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          {show("staff") && <TabsTrigger value="staff" data-testid="users-tab-staff"><UsersIcon className="h-4 w-4 mr-1.5" /> Staff directory{staffRO && <ViewOnlyBadge />}</TabsTrigger>}
          {show("supervisor_targets") && <TabsTrigger value="targets" data-testid="users-tab-supervisor-targets"><Target className="h-4 w-4 mr-1.5" /> Supervisor targets{ro("supervisor_targets") && <ViewOnlyBadge />}</TabsTrigger>}
          {show("collector_targets") && <TabsTrigger value="collector-targets" data-testid="users-tab-collector-targets"><Target className="h-4 w-4 mr-1.5" /> Collector targets{ro("collector_targets") && <ViewOnlyBadge />}</TabsTrigger>}
        </TabsList>
        <TabsContent value="targets" className="mt-4">
          <SupervisorTargetsTab code={code} readOnly={ro("supervisor_targets")} />
        </TabsContent>
        <TabsContent value="collector-targets" className="mt-4">
          <CollectorTargetsTab code={code} readOnly={ro("collector_targets")} />
        </TabsContent>
        <TabsContent value="staff" className="mt-4">
      {staffRO && <ReadOnlyNotice what="the staff directory" testId="users-readonly-notice" className="mb-3" />}
      {/* Phase 167: zone placeholder collectors (GESIL-<ZONE>) holding every ward with no active real collector */}
      <PlaceholderCollectorsPanel code={code} readOnly={staffRO} refreshKey={phRefresh} users={users || []} onReplace={(p) => setReplaceTarget(p)}
        onAssignCollector={(collector, ward) => { setTerritoryPreset({ wardIds: [ward.id], wardName: ward.name }); setTerritoryTarget(collector); }} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="users-totals">
        <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><UserCog className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground">Supervisors</p>
            <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="total-supervisors">
              {totals.supervisors}{totals.supervisorsInactive > 0 && <span className="text-xs font-normal text-muted-foreground"> (+{totals.supervisorsInactive} inactive)</span>}
            </p>
          </div>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-3 flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] flex items-center justify-center"><UserCheck className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground">Tax collectors</p>
            <p className="text-lg font-semibold tabular-nums leading-tight" data-testid="total-collectors">
              {totals.collectors}{totals.collectorsInactive > 0 && <span className="text-xs font-normal text-muted-foreground"> (+{totals.collectorsInactive} inactive)</span>}
              {totals.placeholders > 0 && <span className="text-xs font-normal text-amber-700" data-testid="total-placeholders"> (+{totals.placeholders} placeholder{totals.placeholders === 1 ? "" : "s"})</span>}
            </p>
          </div>
        </CardContent></Card>
        <Card className="shadow-sm col-span-2"><CardContent className="p-3 flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center"><UsersIcon className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground">Showing</p>
            <p className="text-sm font-medium leading-tight" data-testid="users-showing">
              {visibleUsers.length} of {users.length} staff
              {(filtersActive || needle) && <span className="text-muted-foreground font-normal"> — {totals.shownSupervisors} supervisor{totals.shownSupervisors === 1 ? "" : "s"}, {totals.shownCollectors} collector{totals.shownCollectors === 1 ? "" : "s"} match</span>}
            </p>
          </div>
        </CardContent></Card>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="users-filter-bar">
        <Input placeholder="Search name, email, mobile, role…" value={userSearch}
          onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
          className="h-9 w-full sm:w-64" data-testid="users-search-input" />
        <MultiSelect options={zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneF} onChange={changeZoneF}
          allLabel="All zones" noun="zone" placeholder="Search zones…" className="h-9 w-44" testId="users-zone-filter" />
        <MultiSelect options={wardsForZone.map((w) => ({ value: w.id, label: w.ward_name, group: zoneF.length === 1 ? undefined : zoneName[w.zone_id] }))}
          value={wardF} onChange={(ids) => { setWardF(ids); setUserPage(1); }}
          allLabel="All wards" noun="ward" placeholder="Search wards…" className="h-9 w-44" testId="users-ward-filter" />
        <Select value={supF || "all"} onValueChange={(v) => { setSupF(v === "all" ? "" : v); setUserPage(1); }}>
          <SelectTrigger className="h-9 w-48" data-testid="users-supervisor-filter"><SelectValue placeholder="Supervisor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All supervisors</SelectItem>
            {users.filter((u) => u.role === "supervisor" && !u.parent_user_id).sort(cmpName).map((u) => (
              <SelectItem key={u.id} value={u.id}>{staffLabelText(u.name, u.employee_id)}{u.is_active === false ? " (inactive)" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-44" data-testid="users-sort-select">
            <span className="flex items-center gap-1.5 truncate"><ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" /> <SelectValue /></span>
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filtersActive || needle) && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={resetFilters} data-testid="users-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <Switch checked={hideInactive} onCheckedChange={setHide} data-testid="hide-inactive-toggle" />
          <span className="flex items-center gap-1.5">
            {hideInactive ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
            Hide inactive
          </span>
        </label>
        {inactiveCount > 0 && (
          hideInactive ? (
            <Badge variant="secondary" className="font-normal" data-testid="hidden-inactive-badge">
              {inactiveCount} inactive user{inactiveCount === 1 ? "" : "s"} hidden
            </Badge>
          ) : (
            <Badge variant="outline" className="font-normal text-muted-foreground" data-testid="inactive-count-badge">
              {inactiveCount} inactive
            </Badge>
          )
        )}
        {hideInactive && inactiveCount > 0 && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setHide(false)} data-testid="show-inactive-button">
            <Eye className="h-3.5 w-3.5 mr-1" /> Show them
          </Button>
        )}
      </div>

      {/* Phase 159: bulk selection bar — only when something is ticked */}
      {!staffRO && selectedVisible.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 mb-3"
          role="status" data-testid="users-selection-bar">
          <span className="text-sm font-medium tabular-nums" data-testid="users-selected-count">
            {selectedVisible.length} selected
          </span>
          <span className="text-xs text-muted-foreground" data-testid="users-selected-hint">
            — {selectedCollectorsWithTerritory} collector{selectedCollectorsWithTerritory === 1 ? "" : "s"} with wards / mohallas to clear
            {selectedVisible.length - selectedCollectorsWithTerritory > 0 && `; ${selectedVisible.length - selectedCollectorsWithTerritory} will be skipped (supervisors keep their zones, unmapped collectors have nothing to clear)`}
          </span>
          <span className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearSelection} data-testid="users-clear-selection">
              <X className="h-3.5 w-3.5 mr-1" /> Clear selection
            </Button>
            <Button variant="destructive" size="sm" className="h-8" onClick={() => setBulkUnassignOpen(true)}
              disabled={selectedCollectorsWithTerritory === 0}
              title={selectedCollectorsWithTerritory === 0 ? "None of the selected users has ward / mohalla territory" : "Remove every ward and mohalla from the selected collectors"}
              data-testid="users-bulk-unassign-button">
              <MapPinOff className="h-3.5 w-3.5 mr-1.5" /> Unassign All Territories
            </Button>
          </span>
        </div>
      )}

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                {!staffRO && (
                  <TableHead className="w-10 pr-0">
                    <Checkbox
                      checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                      onCheckedChange={(on) => togglePage(on === true)}
                      disabled={selectableIds.length === 0}
                      aria-label={`Select all ${selectableIds.length} supervisors and collectors on this page`}
                      data-testid="users-select-all" />
                  </TableHead>
                )}
                <TableHead>Name</TableHead>
                <TableHead className="hidden lg:table-cell">Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Mobile</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Approver</TableHead>
                <TableHead className="text-right">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow data-testid="users-empty-row">
                  <TableCell colSpan={staffRO ? 8 : 9} className="text-center text-sm text-muted-foreground py-8">
                    {filtersActive
                      ? "No staff match the selected zone / ward / supervisor."
                      : needle
                      ? `No users match "${userSearch.trim()}".`
                      : hideInactive && inactiveCount > 0
                        ? `No active users to show — ${inactiveCount} inactive user${inactiveCount === 1 ? " is" : "s are"} hidden.`
                        : "No users yet."}
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map(({ u, isSub }) => {
                const isProtectedAdmin = u.role === "municipal_admin" && !u.parent_user_id;
                const canSelect = selectable(u);
                const isSelected = canSelect && selected.has(u.id);
                return (
                <TableRow key={u.id} className={`hover:bg-muted/60 ${isSelected ? "bg-primary/5" : isSub ? "bg-muted/30" : ""}`}
                  data-state={isSelected ? "selected" : undefined}
                  data-testid={isSub ? `sub-user-row-${u.email}` : `user-row-${u.email}`}>
                  {!staffRO && (
                    <TableCell className="w-10 pr-0">
                      {canSelect ? (
                        <Checkbox checked={isSelected} onCheckedChange={(on) => toggleSelect(u.id, on === true)}
                          aria-label={`Select ${u.name}`} data-testid={`user-select-${u.email}`} />
                      ) : (
                        <span className="sr-only" data-testid={`user-select-na-${u.email}`}>Not selectable</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <span className={`flex items-center gap-2 ${isSub ? "pl-5" : ""}`}>
                      {isSub && <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <StaffAvatar code={code} userId={u.id} name={u.name} photoUrl={u.photo_url || null} size={isSub ? "xs" : "sm"} testId={`user-avatar-${u.email}`} />
                      <span className="min-w-0">
                        {u.name}{u.role !== "citizen" && !u.is_placeholder && <EmpCode code={u.employee_id} testId={`user-emp-code-${u.email}`} />}
                        {u.is_placeholder && <PlaceholderBadge className="ml-1.5 align-middle" testId={`user-placeholder-badge-${u.email}`} />}
                        {isSub && (
                          <span className="block text-[10px] font-normal text-muted-foreground">
                            Sub-user of <StaffLabel code={code} userId={u.parent_user_id} name={u.parent_user_name} />
                          </span>
                        )}
                        {MAPPING_RULES[u.role] && (
                          u.reports_to_name ? (
                            <span className="block text-[10px] font-normal text-muted-foreground" data-testid={`user-reports-to-${u.email}`}>
                              Reports to <StaffLabel code={code} userId={u.reports_to_id} name={u.reports_to_name} />
                            </span>
                          ) : (
                            <span className="block text-[10px] font-normal text-amber-600" data-testid={`user-not-mapped-${u.email}`}>
                              Not mapped
                            </span>
                          )
                        )}
                        {(() => { const t = territoryLine(u); return t ? (
                          <span className="flex items-center gap-1 text-[10px] font-normal text-muted-foreground" data-testid={`user-territory-line-${u.email}`}>
                            <MapPin className="h-3 w-3 shrink-0" /> {t.icon === "zone" ? "Zone" : "Ward"}: {t.text}
                          </span>
                        ) : null; })()}
                        <span className="block lg:hidden text-[11px] font-normal text-muted-foreground truncate">{u.email}</span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm">
                    {u.is_placeholder ? (
                      <span data-testid={`user-placeholder-role-${u.email}`}>
                        Tax collector
                        <span className="block text-[10px] text-amber-700">
                          Placeholder · {u.placeholder_kind === "zone"
                            ? `${u.placeholder_zone_id ? (/zone/i.test(u.placeholder_zone_name || "") ? u.placeholder_zone_name : `Zone ${u.placeholder_zone_name}`) : "wards without a zone"} · ${(u.ward_ids || []).length} ward${(u.ward_ids || []).length === 1 ? "" : "s"} with no collector`
                            : u.placeholder_ward_name || "ward with receipts, no collector"}
                        </span>
                      </span>
                    ) : u.custom_role_name ? (
                      <span data-testid={`user-custom-role-${u.email}`}>
                        {u.custom_role_name}
                        <span className="block text-[10px] text-muted-foreground">Custom role</span>
                      </span>
                    ) : (
                      <span className="capitalize">{u.role.replace(/_/g, " ")}</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{u.is_placeholder ? <span className="text-muted-foreground">—</span> : (u.mobile || "-")}</TableCell>
                  <TableCell><StatusBadge status={u.is_active ? "active" : "inactive"} /></TableCell>
                  <TableCell className="text-center">
                    {u.is_placeholder ? (
                      <span className="text-xs text-muted-foreground" title="Placeholder collectors cannot approve" data-testid={`user-approver-na-${u.email}`}>—</span>
                    ) : u.role === "municipal_admin" ? (
                      <Badge variant="outline" className="text-[10px]">Always</Badge>
                    ) : STAFF_ROLES.includes(u.role) ? (
                      <Switch checked={!!u.can_approve} onCheckedChange={() => toggleApprover(u)} disabled={staffRO}
                        data-testid={`user-approver-toggle-${u.email}`} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isProtectedAdmin ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                        title="Only the Super Admin can activate or deactivate Municipal Admin accounts"
                        data-testid={`user-toggle-locked-${u.email}`}>
                        <Lock className="h-3.5 w-3.5" /> Super Admin only
                      </span>
                    ) : (
                      <Switch checked={!!u.is_active} onCheckedChange={() => toggle(u)} disabled={staffRO} data-testid={`user-toggle-${u.email}`} />
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {u.is_placeholder ? (
                      <>
                        {!staffRO && (
                          <Button variant="outline" size="sm" className="h-8 text-xs mr-1" title="Merge into an existing collector or convert into the real staff member"
                            onClick={() => setReplaceTarget(u)} data-testid={`user-replace-placeholder-${u.email}`}>
                            <Replace className="h-3.5 w-3.5 mr-1" /> Replace
                          </Button>
                        )}
                        {!staffRO && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Assign territory (zones / wards / mohallas)"
                            onClick={() => setTerritoryTarget(u)} data-testid={`user-territory-${u.email}`}>
                            <MapPin className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Activity & audit history"
                          onClick={() => setActivityTarget(u)} data-testid={`user-activity-${u.email}`}>
                          <History className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                    <>
                    {!isProtectedAdmin && !staffRO && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit user details"
                        onClick={() => setEditTarget(u)} data-testid={`user-edit-${u.email}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {MAPPING_RULES[u.role] && !staffRO && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Assign territory (zones / wards / mohallas)"
                        onClick={() => setTerritoryTarget(u)} data-testid={`user-territory-${u.email}`}>
                        <MapPin className="h-4 w-4" />
                      </Button>
                    )}
                    {!isSub && MAPPING_RULES[u.role] && !staffRO && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Change mapping"
                        onClick={() => setMappingTarget(u)} data-testid={`user-mapping-${u.email}`}>
                        <GitBranch className="h-4 w-4" />
                      </Button>
                    )}
                    {u.role !== "super_admin" && !staffRO && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Menu access"
                        onClick={() => setAccessTarget(u)} data-testid={`user-access-${u.email}`}>
                        <ShieldCheck className="h-4 w-4" />
                      </Button>
                    )}
                    {STAFF_ROLES.includes(u.role) && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Activity & audit history (kept even after deactivation)"
                        onClick={() => setActivityTarget(u)} data-testid={`user-activity-${u.email}`}>
                        <History className="h-4 w-4" />
                      </Button>
                    )}
                    {!isSub && STAFF_ROLES.includes(u.role) && !staffRO && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Add sub-user"
                        onClick={() => setSubParent(u)} data-testid={`user-add-sub-${u.email}`}>
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    )}
                    {!staffRO && <Button variant="ghost" size="icon" className="h-8 w-8" title="Reset password"
                      onClick={() => setResetTarget(u)} data-testid={`user-reset-password-${u.email}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>}
                    </>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {rows.length > 0 && (
        <PaginationBar page={curPage} limit={userLimit} total={rows.length}
          onPageChange={setUserPage} onLimitChange={(n) => { setUserLimit(n); setUserPage(1); }} testId="users-pagination" />
      )}
      <p className="text-xs text-muted-foreground mt-3">
        Sub-users work under a main user with the same role and inherit the parent's menu access at creation.
        "Approver" users appear in the collector's "Send approval to" list for reassessments and surveys.
      </p>
        </TabsContent>
      </Tabs>
      )}

      <ResetPasswordDialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}
        targetLabel={resetTarget ? `${resetTarget.name} (${resetTarget.email})` : ""}
        onSubmit={async (pw) => {
          const r = await api.post(`/t/${code}/users/${resetTarget.id}/reset-password`, { new_password: pw });
          toast.success(r.data.message || "Password reset");
        }} />
      <EditUserDialog code={code} user={editTarget} customFields={cfg.custom_fields}
        onOpenChange={(v) => !v && setEditTarget(null)} onSaved={load} />
      <AddSubUserDialog code={code} parent={subParent} onOpenChange={(v) => !v && setSubParent(null)} onCreated={load} />
      <MappingDialog code={code} user={mappingTarget} allUsers={users || []} customRoles={cfg.custom_roles}
        onOpenChange={(v) => !v && setMappingTarget(null)} onSaved={load} />
      <TerritoryDialog code={code} user={territoryTarget} open={!!territoryTarget} presetWardIds={territoryPreset?.wardIds} presetWardName={territoryPreset?.wardName}
        onOpenChange={(v) => { if (!v) { setTerritoryTarget(null); setTerritoryPreset(null); } }}
        onSaved={() => { load(); setPhRefresh((k) => k + 1); }} />
      <BulkUnassignDialog code={code} userIds={selectedVisible} open={bulkUnassignOpen}
        onOpenChange={setBulkUnassignOpen} onDone={() => { clearSelection(); load(); }} />
      <StaffActivitySheet code={code} user={activityTarget} onClose={() => setActivityTarget(null)} />
      <ReplacePlaceholderDialog code={code} placeholder={replaceTarget} users={users || []} open={!!replaceTarget}
        onOpenChange={(v) => !v && setReplaceTarget(null)} onDone={() => { load(); setPhRefresh((k) => k + 1); }} />
      <UserAccessDialog code={code} user={accessTarget} open={!!accessTarget}
        onOpenChange={(v) => !v && setAccessTarget(null)} onSaved={load} />
    </div>
  );
}
